import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Cell,
} from "recharts";
import { apiGet } from "../../lib/api";

/**
 * Email stats response from API
 */
interface EmailStats {
  last24Hours: { sent: number; opened: number; clicked: number; bounced: number };
  last7Days: { sent: number; opened: number; clicked: number; bounced: number };
  last30Days: { sent: number; opened: number; clicked: number; bounced: number };
  byEmailType: Record<string, { sent: number; openRate: number; clickRate: number }>;
  bySequence: Record<string, { enrolled: number; completed: number; exited: number; active: number }>;
}

/**
 * Recent email from API
 */
interface RecentEmail {
  id: string;
  profileId: string;
  recipientEmail: string;
  emailType: string;
  sequenceName: string | null;
  subject: string;
  sentAt: string;
  openedAt: string | null;
  clickedAt: string | null;
  bouncedAt: string | null;
  status: "sent" | "opened" | "clicked" | "bounced";
}

interface RecentEmailsResponse {
  emails: RecentEmail[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * EmailDashboardPage - Admin email management dashboard.
 *
 * Features:
 * - Email send statistics (24h, 7d, 30d)
 * - Open/click rate charts
 * - Recent email history
 * - Sequence enrollment metrics
 */
export function EmailDashboardPage() {
  const [stats, setStats] = useState<EmailStats | null>(null);
  const [recentEmails, setRecentEmails] = useState<RecentEmail[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<"24h" | "7d" | "30d">("7d");

  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      setError(null);

      try {
        const [statsRes, emailsRes] = await Promise.all([
          apiGet<EmailStats>("/api/admin/emails/stats"),
          apiGet<RecentEmailsResponse>("/api/admin/emails/recent?limit=10"),
        ]);

        if (statsRes.error) {
          throw new Error(statsRes.error.message);
        }
        if (emailsRes.error) {
          throw new Error(emailsRes.error.message);
        }

        setStats(statsRes.data ?? null);
        setRecentEmails(emailsRes.data?.emails ?? []);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to load email data");
      } finally {
        setLoading(false);
      }
    }

    fetchData();
  }, []);

  // Get period stats based on selection
  const periodStats = stats
    ? selectedPeriod === "24h"
      ? stats.last24Hours
      : selectedPeriod === "7d"
        ? stats.last7Days
        : stats.last30Days
    : null;

  // Calculate rates
  const openRate = periodStats && periodStats.sent > 0
    ? Math.round((periodStats.opened / periodStats.sent) * 100)
    : 0;
  const clickRate = periodStats && periodStats.sent > 0
    ? Math.round((periodStats.clicked / periodStats.sent) * 100)
    : 0;

  // Prepare chart data for email types
  const emailTypeChartData = stats
    ? Object.entries(stats.byEmailType)
        .map(([name, data]) => ({
          name: formatEmailType(name),
          sent: data.sent,
          openRate: Math.round(data.openRate * 100),
          clickRate: Math.round(data.clickRate * 100),
        }))
        .sort((a, b) => b.sent - a.sent)
        .slice(0, 8)
    : [];

  // Prepare sequence data
  const sequenceData = stats
    ? Object.entries(stats.bySequence).map(([name, data]) => ({
        name: formatSequenceName(name),
        ...data,
        completionRate: data.enrolled > 0
          ? Math.round((data.completed / data.enrolled) * 100)
          : 0,
      }))
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-magic-500 border-t-transparent" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <p className="text-red-600 font-medium">Error loading email data</p>
        <p className="text-red-500 text-sm mt-1">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Dashboard</h1>
          <p className="mt-1 text-sm text-gray-500">
            Monitor email performance and engagement
          </p>
        </div>

        {/* Period selector */}
        <div className="flex gap-2">
          {(["24h", "7d", "30d"] as const).map((period) => (
            <button
              key={period}
              onClick={() => setSelectedPeriod(period)}
              className={`
                px-4 py-2 text-sm font-medium rounded-lg transition-colors
                ${
                  selectedPeriod === period
                    ? "bg-magic-600 text-white"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }
              `}
            >
              {period === "24h" ? "24 Hours" : period === "7d" ? "7 Days" : "30 Days"}
            </button>
          ))}
        </div>
      </div>

      {/* Primary Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Sent"
          value={periodStats?.sent ?? 0}
          icon={<SendIcon />}
          color="blue"
        />
        <StatCard
          title="Opened"
          value={periodStats?.opened ?? 0}
          subtitle={`${openRate}% open rate`}
          icon={<OpenIcon />}
          color="green"
        />
        <StatCard
          title="Clicked"
          value={periodStats?.clicked ?? 0}
          subtitle={`${clickRate}% click rate`}
          icon={<ClickIcon />}
          color="purple"
        />
        <StatCard
          title="Bounced"
          value={periodStats?.bounced ?? 0}
          icon={<BounceIcon />}
          color="red"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Email Types Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Emails by Type
          </h2>
          {emailTypeChartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emailTypeChartData} layout="vertical">
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis type="number" tick={{ fontSize: 12 }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={100}
                    tick={{ fontSize: 11 }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="sent" fill="#8b5cf6" radius={[0, 4, 4, 0]}>
                    {emailTypeChartData.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={CHART_COLORS[index % CHART_COLORS.length]}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="No email data yet" />
          )}
        </div>

        {/* Open/Click Rates */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Engagement Rates by Type
          </h2>
          {emailTypeChartData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={emailTypeChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                  <XAxis
                    dataKey="name"
                    tick={{ fontSize: 10 }}
                    angle={-45}
                    textAnchor="end"
                    height={60}
                  />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `${v}%`}
                  />
                  <Tooltip
                    formatter={(value) => [`${value}%`, ""]}
                    contentStyle={{
                      backgroundColor: "white",
                      border: "1px solid #e5e7eb",
                      borderRadius: "8px",
                      fontSize: "12px",
                    }}
                  />
                  <Bar dataKey="openRate" name="Open Rate" fill="#10b981" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="clickRate" name="Click Rate" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <EmptyState message="No email data yet" />
          )}
        </div>
      </div>

      {/* Sequences Section */}
      {sequenceData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Email Sequences
            </h2>
            <Link
              to="/admin/emails/sequences"
              className="text-sm text-magic-600 hover:text-magic-700 font-medium"
            >
              View All →
            </Link>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {sequenceData.map((seq) => (
              <div
                key={seq.name}
                className="p-4 rounded-lg bg-gray-50 border border-gray-100"
              >
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-medium text-gray-900">{seq.name}</h3>
                  <span className="text-xs font-medium px-2 py-1 rounded-full bg-magic-100 text-magic-700">
                    {seq.completionRate}% completion
                  </span>
                </div>
                <div className="grid grid-cols-4 gap-2 text-center">
                  <div>
                    <p className="text-lg font-semibold text-gray-900">{seq.enrolled}</p>
                    <p className="text-xs text-gray-500">Enrolled</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-green-600">{seq.active}</p>
                    <p className="text-xs text-gray-500">Active</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-blue-600">{seq.completed}</p>
                    <p className="text-xs text-gray-500">Completed</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold text-gray-400">{seq.exited}</p>
                    <p className="text-xs text-gray-500">Exited</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Recent emails table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Recent Emails</h2>
          <Link
            to="/admin/emails/history"
            className="text-sm text-magic-600 hover:text-magic-700 font-medium"
          >
            View All →
          </Link>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Recipient
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Subject
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Sent
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {recentEmails.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500">
                    <EmailIcon />
                    <p className="mt-2 text-sm">No emails sent yet</p>
                  </td>
                </tr>
              ) : (
                recentEmails.map((email) => (
                  <tr key={email.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/users/${email.profileId}/emails`}
                        className="text-sm font-medium text-gray-900 hover:text-magic-600"
                      >
                        {email.recipientEmail}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 max-w-xs truncate">
                      {email.subject}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-xs font-medium px-2 py-1 rounded-full bg-gray-100 text-gray-700">
                        {formatEmailType(email.emailType)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <StatusBadge status={email.status} />
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {formatDate(email.sentAt)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// Chart colors
const CHART_COLORS = [
  "#8b5cf6", // purple
  "#10b981", // green
  "#f59e0b", // amber
  "#3b82f6", // blue
  "#ec4899", // pink
  "#6366f1", // indigo
  "#14b8a6", // teal
  "#f97316", // orange
];

// Helper functions
function formatEmailType(type: string): string {
  return type
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatSequenceName(name: string): string {
  return name
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) {
    return `${diffMins}m ago`;
  }
  if (diffHours < 24) {
    return `${diffHours}h ago`;
  }
  if (diffDays < 7) {
    return `${diffDays}d ago`;
  }
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

// Components
interface StatCardProps {
  title: string;
  value: number;
  subtitle?: string;
  icon: React.ReactNode;
  color: "blue" | "green" | "purple" | "red";
}

function StatCard({ title, value, subtitle, icon, color }: StatCardProps) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    purple: "bg-purple-50 text-purple-600",
    red: "bg-red-50 text-red-600",
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-500">{title}</p>
          <p className="mt-2 text-3xl font-bold text-gray-900">
            {value.toLocaleString()}
          </p>
          {subtitle && (
            <p className="mt-1 text-xs text-gray-400">{subtitle}</p>
          )}
        </div>
        <div className={`p-2 rounded-lg ${colors[color]}`}>
          {icon}
        </div>
      </div>
    </div>
  );
}

interface StatusBadgeProps {
  status: "sent" | "opened" | "clicked" | "bounced";
}

function StatusBadge({ status }: StatusBadgeProps) {
  const styles = {
    sent: "bg-gray-100 text-gray-700",
    opened: "bg-green-100 text-green-700",
    clicked: "bg-purple-100 text-purple-700",
    bounced: "bg-red-100 text-red-700",
  };

  return (
    <span className={`text-xs font-medium px-2 py-1 rounded-full ${styles[status]}`}>
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="h-64 flex items-center justify-center">
      <p className="text-sm text-gray-500">{message}</p>
    </div>
  );
}

// Icons
function SendIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M3.105 2.289a.75.75 0 00-.826.95l1.414 4.925A1.5 1.5 0 005.135 9.25h6.115a.75.75 0 010 1.5H5.135a1.5 1.5 0 00-1.442 1.086l-1.414 4.926a.75.75 0 00.826.95 28.896 28.896 0 0015.293-7.154.75.75 0 000-1.115A28.897 28.897 0 003.105 2.289z" />
    </svg>
  );
}

function OpenIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
      <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
    </svg>
  );
}

function ClickIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path d="M6.672 1.911a1 1 0 10-1.932.518l.259.966a1 1 0 001.932-.518l-.26-.966zM2.429 4.74a1 1 0 10-.517 1.932l.966.259a1 1 0 00.517-1.932l-.966-.26zm8.814-.569a1 1 0 00-1.415-1.414l-.707.707a1 1 0 101.414 1.415l.708-.708zm-7.071 7.072l.707-.707A1 1 0 003.465 9.12l-.708.707a1 1 0 001.415 1.415zm3.2-5.171a1 1 0 00-1.3 1.3l4 10a1 1 0 001.823.075l1.38-2.759 3.018 3.02a1 1 0 001.414-1.415l-3.019-3.02 2.76-1.379a1 1 0 00-.076-1.822l-10-4z" />
    </svg>
  );
}

function BounceIcon() {
  return (
    <svg className="w-5 h-5" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-5a.75.75 0 01.75.75v4.5a.75.75 0 01-1.5 0v-4.5A.75.75 0 0110 5zm0 10a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
    </svg>
  );
}

function EmailIcon() {
  return (
    <svg
      className="w-12 h-12 text-gray-300 mx-auto"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );
}

export default EmailDashboardPage;
